import React, { useState, useEffect } from "react";
import { bankingPartnerAPI } from "../../../services/AdminPortal/api";
import { SearchIcon } from "@heroicons/react/solid";
import { toast } from "react-toastify";

export default function InsurancesTab({ partnerData }) {
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch insurances for this specific banking partner
  const fetchInsurances = async () => {
    if (!partnerData?._id) return;
    
    setLoading(true);
    setError(null);
    try {
      const res = await bankingPartnerAPI.getById(partnerData._id);
      const data = res.data || res;
      
      // Map backend structure to table structure
      const mappedInsurances = (data.insuranceDetails || []).map((ins, idx) => ({
        id: idx,
        insurance: ins.insuranceType + (ins.insuranceCategory ? ` / ${ins.insuranceCategory}` : ""),
        category: ins.insuranceCategory,
        eligibility: ins.insuranceEligibility,
        amount: ins.insuranceAmount,
        linkedLoan: ins.linkedLoan || "",
        status: ins.status || "Active",
      }));
      
      setInsurances(mappedInsurances);
    } catch (err) {
      console.error("Error fetching insurances:", err);
      setError(err.message || "Failed to fetch insurances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsurances();
  }, [partnerData?._id]);

  // Handler to update insurance status
  const handleInsuranceStatusChange = async (ins, newStatus, idx) => {
    if (!partnerData?._id) return;
    
    try {
      await bankingPartnerAPI.updateInsuranceStatus(partnerData._id, idx, newStatus);
      toast.success("Insurance status updated successfully");
      // Refresh the insurances data
      fetchInsurances();
    } catch (err) {
      console.error("Insurance status update error:", err);
      toast.error(err.message || "Failed to update insurance status");
    }
  };

  const filteredInsurances = insurances.filter((ins) => {
    if (!searchTerm) {
      return true;
    }
    const searchLower = searchTerm.toLowerCase();
    return (
      ins.insurance?.toLowerCase().includes(searchLower) ||
      ins.category?.toLowerCase().includes(searchLower) ||
      ins.eligibility?.toLowerCase().includes(searchLower) ||
      ins.amount?.toString().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
        <div className="text-center py-8 text-red-400">
          <p>Error loading insurances: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="relative w-full sm:w-auto">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search Insurance Products"
            className="bg-black border border-[#333333] rounded-full py-2 pl-10 pr-4 text-white w-full sm:w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <span className="text-sm text-gray-400">
          {insurances.length} insurance{insurances.length !== 1 ? "s" : ""}{" "}
          found
        </span>
      </div>

      {insurances.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No insurance products available for this banking partner.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#333333] rounded-lg scrollbar-hide">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead className="bg-[#131313] text-[#F3F3F3]">
              <tr>
               
                <th className="px-4 py-3 text-left border-x border-[#333333]">
                  INSURANCE
                </th>
                <th className="px-4 py-3 text-left border-x border-[#333333]">
                  CATEGORY
                </th>
                <th className="px-4 py-3 text-left border-x border-[#333333]">
                  ELIGIBILITY
                </th>
                <th className="px-4 py-3 text-left border-x border-[#333333]">
                  AMOUNT
                </th>
                <th className="px-4 py-3 text-left border-x border-[#333333] rounded-tr-lg">
                  STATUS
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#131313] text-white">
              {filteredInsurances.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-400">
                    {insurances.length > 0
                      ? "No matching insurance products found."
                      : "No insurance data available."}
                  </td>
                </tr>
              ) : (
                filteredInsurances.map((ins, idx) => {
                  const isLast = idx === filteredInsurances.length - 1;
                  return (
                    <tr key={ins.id || idx} className="hover:bg-gray-800">
                      
                      <td className="px-4 py-3 border-x border-[#333333]">
                        {ins.insurance}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]">
                        {ins.category}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]">
                        {ins.eligibility}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]">
                        ₹{ins.amount?.toLocaleString("en-IN")}
                      </td>
                      <td
                        className={`px-4 py-3 border-x border-[#333333] ${
                          isLast ? "rounded-br-lg" : ""
                        }`}
                      >
                        <select
                          value={ins.status}
                          onChange={(e) =>
                            handleInsuranceStatusChange(
                              ins,
                              e.target.value,
                              idx
                            )
                          }
                          className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-yellow-400 border shadow-sm
                            ${
                              ins.status === "Active"
                                ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                            }`}
                          style={{ minWidth: 110, cursor: "pointer" }}
                        >
                          <option value="Active">Active</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
